import { gearGroups, selectGear, gearUrl, analyticsPayload, GEAR_LIMIT } from '#compatible-gear-state';

/**
 * PDP section "Passt dazu" (sections/compatible-gear.liquid). The section
 * ships a hidden shell; this element fills it:
 *   1. #compatible-gear-state names the groups of the source product.
 *   2. When the host nears the viewport, the first group's candidates are
 *      fetched from sections/compatible-gear-feed (type collection sorted by
 *      best selling, product_type search as the fallback) and judged. An empty
 *      group is dropped and the next one tried; the shell only shows once a
 *      group has matches, so most page views cost one request.
 *   3. Every further tab loads on first use (pointer or focus prefetches it).
 * A failed feed drops its group; it never shows an error on a product page.
 */
if (!customElements.get('compatible-gear')) {
  customElements.define('compatible-gear', class extends HTMLElement {
    connectedCallback() {
      try {
        this.source = JSON.parse(this.querySelector('[data-gear-source]').textContent);
        this.strings = JSON.parse(this.querySelector('[data-gear-strings]').textContent);
      } catch { return; }
      // Optional: the curated binding system of a plate ski or a race binding.
      try { this.source.system = JSON.parse(this.querySelector('[data-gear-system]')?.textContent || 'null'); } catch { this.source.system = null; }
      this.cartIds = (this.dataset.cartIds || '').split(',').filter(Boolean);
      this.limit = Number(this.dataset.limit) || GEAR_LIMIT;
      this.groups = gearGroups(this.source).map((item) => ({ item, state: 'idle' }));
      if (!this.groups.length) return;
      this.abort = new AbortController();
      this.seen = new Set();
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.starter = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.start();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.starter.observe(this);
    }

    disconnectedCallback() {
      this.starter?.disconnect();
      this.viewer?.disconnect();
      this.abort?.abort();
    }

    get root() { return this.querySelector('[data-gear-root]'); }

    /** Finds the first group with matches, then reveals the shell with every group not yet proven empty. */
    async start() {
      for (const group of this.groups) {
        await this.load(group);
        if (group.state === 'ready') break;
      }
      this.groups = this.groups.filter((group) => group.state !== 'empty');
      if (!this.isConnected || !this.groups.length) return;
      this.viewer = new IntersectionObserver((entries) => this.onView(entries), { threshold: 0.5 });
      this.renderTabs();
      this.root.hidden = false;
      this.select(this.groups[0]);
    }

    feedUrls(item) {
      const section = this.dataset.feedSection;
      const urls = [];
      if (item.collection) {
        const url = new URL(`${this.dataset.collectionsUrl}/${item.collection}`, location.origin);
        url.searchParams.set('sort_by', 'best-selling');
        url.searchParams.set('filter.v.availability', '1');
        url.searchParams.set('section_id', section);
        urls.push(url);
      }
      if (!item.searchFallback) return urls;
      const search = new URL(this.dataset.searchUrl, location.origin);
      search.searchParams.set('q', item.types.map((type) => `product_type:"${type}"`).join(' OR '));
      search.searchParams.set('type', 'product');
      search.searchParams.set('options[unavailable_products]', 'hide');
      search.searchParams.set('section_id', section);
      urls.push(search);
      return urls;
    }

    /** Aborts with the element and after 15 s; browsers without AbortSignal.any (Safari before 17.4) keep the first half. */
    signal() {
      return AbortSignal.any ? AbortSignal.any([this.abort.signal, AbortSignal.timeout(15000)]) : this.abort.signal;
    }

    async candidates(item) {
      for (const url of this.feedUrls(item)) {
        try {
          const response = await fetch(url, { signal: this.signal() });
          if (!response.ok) continue;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const found = [...doc.querySelectorAll('template[data-gear-candidate]')].flatMap((template) => {
            try { return [{ ...JSON.parse(template.dataset.gearFacts), template }]; } catch { return []; }
          });
          if (found.length) return found;
        } catch { /* next source */ }
      }
      return [];
    }

    load(group) {
      group.loading ||= this.candidates(group.item).then((candidates) => {
        group.matches = selectGear(this.source, group.item, candidates, { cartIds: this.cartIds, limit: this.limit });
        group.state = group.matches.length ? 'ready' : 'empty';
      });
      return group.loading;
    }

    renderTabs() {
      const list = this.querySelector('[data-gear-tablist]');
      list.replaceChildren(...this.groups.map((group, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.role = 'tab';
        tab.id = `${this.id || 'compatible-gear'}-tab-${group.item.key}`;
        tab.className = 'collection-tabs__tab text-body-14-semibold';
        tab.textContent = this.strings.groups[group.item.key] || group.item.key;
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = index === 0 ? 0 : -1;
        tab.addEventListener('pointerenter', () => this.load(group), { once: true });
        tab.addEventListener('focus', () => this.load(group), { once: true });
        group.tab = tab;
        return tab;
      }));
      list.hidden = this.groups.length < 2;
    }

    async select(group) {
      this.current = group;
      for (const other of this.groups) {
        other.tab.setAttribute('aria-selected', String(other === group));
        other.tab.tabIndex = other === group ? 0 : -1;
        if (other.panel) other.panel.hidden = other !== group;
      }
      this.querySelector('[data-gear-note]').textContent = (this.strings.notes[group.item.rule] || '').replace('[set]', this.source.system?.name ?? '');
      group.tab.setAttribute('aria-busy', 'true');
      await this.load(group);
      group.tab.removeAttribute('aria-busy');
      if (!this.isConnected) return;
      if (group.state === 'empty') { this.drop(group); return; }
      if (!group.panel) this.renderPanel(group);
      group.panel.hidden = this.current !== group;
      if (this.current === group) {
        group.panel.querySelector('swiper-component')?.swiper?.update();
        this.querySelector('[data-gear-status]').textContent = `${group.tab.textContent}: ${group.matches.length}`;
      }
    }

    /** A tab that turned out empty on first use disappears; the shell goes with the last one. */
    drop(group) {
      const index = this.groups.indexOf(group);
      this.groups.splice(index, 1);
      group.tab.remove();
      if (!this.groups.length) { this.root.hidden = true; return; }
      this.querySelector('[data-gear-tablist]').hidden = this.groups.length < 2;
      if (this.current === group) this.select(this.groups[Math.min(index, this.groups.length - 1)]);
    }

    renderPanel(group) {
      const panel = document.createElement('div');
      panel.className = 'collection-tabs__panel';
      panel.role = 'tabpanel';
      panel.tabIndex = 0;
      panel.setAttribute('aria-labelledby', group.tab.id);
      const body = this.querySelector('template[data-gear-panel]').content.cloneNode(true);
      const track = body.querySelector('.swiper-wrapper');
      for (const match of group.matches) {
        const slide = document.importNode(match.candidate.template.content, true).querySelector('.swiper-slide');
        if (!slide) continue;
        for (const link of slide.querySelectorAll('a[href]')) link.setAttribute('href', gearUrl(link.getAttribute('href'), match.fit, location.origin));
        const text = match.fit.kind && this.strings.fits[group.item.rule];
        if (text) {
          const fit = slide.querySelector('[data-gear-fit]');
          fit.textContent = text.replace('[mm]', match.fit.brakeMm ?? '');
          fit.hidden = false;
        }
        slide.gearMatch = { group, match };
        track.append(slide);
        this.viewer.observe(slide);
      }
      // Children first, then insert: <swiper-component> initialises on connect and reads its slides there.
      panel.append(body);
      this.querySelector('[data-gear-panels]').append(panel);
      group.panel = panel;
    }

    onClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (tab) { this.select(this.groups.find((group) => group.tab === tab)); return; }
      const slide = event.target.closest('.compatible-gear__slide');
      if (slide?.gearMatch && event.target.closest('a[href]')) this.track('click', slide.gearMatch);
    }

    onKeydown(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = this.groups.findIndex((group) => group.tab === tab);
      const last = this.groups.length - 1;
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? last
        : event.key === 'ArrowRight' ? (index === last ? 0 : index + 1) : (index === 0 ? last : index - 1);
      this.groups[next].tab.focus();
      this.select(this.groups[next]);
    }

    onView(entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting || !entry.target.gearMatch) continue;
        const key = `${entry.target.gearMatch.group.item.key}:${entry.target.dataset.gearId}`;
        if (!this.seen.has(key) && this.track('view', entry.target.gearMatch)) this.seen.add(key);
      }
    }

    /** Same consent gate and channel as the cart recommendations. */
    track(action, { group, match }) {
      try {
        const shopify = window.Shopify;
        if (!shopify?.customerPrivacy?.analyticsProcessingAllowed?.() || !shopify.analytics?.publish) return false;
        shopify.analytics.publish('compatible_gear', analyticsPayload(action, this.source, group.item, match))?.catch?.(() => {});
        return true;
      } catch { return false; }
    }
  });
}
